import React, { useEffect, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, Avatar, Chip } from "@material-ui/core";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  container: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F1F5F9"}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 18px",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F1F5F9"}`,
  },
  headerIcon: {
    color: "#F59E0B",
    fontSize: 22,
  },
  headerTitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
  },
  body: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 18px",
    flex: 1,
    gap: 16,
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flex: 1,
    minWidth: 0,
  },
  avatarContainer: {
    position: "relative",
    flexShrink: 0,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#F1F5F9",
    border: `2px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
  },
  avatarIcon: {
    fontSize: 40,
    color: "#35af9a",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
    flex: 1,
  },
  userName: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    lineHeight: 1.2,
  },
  queuesContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
  },
  queueChip: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 11,
    fontWeight: 500,
    height: 22,
    borderRadius: 6,
    backgroundColor: theme.palette.type === "dark" ? "rgba(53,175,154,0.2)" : "#EBF5F3",
    color: "#35af9a",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(53,175,154,0.3)" : "#D1FAE5"}`,
  },
  noQueueText: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    fontStyle: "italic",
  },
  emptyMessageWrapper: {
    width: "100%",
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  emptyMessage: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    textAlign: "center",
  },
  rightSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "center",
    flexShrink: 0,
  },
  userCount: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 28,
    fontWeight: 700,
    color: "#35af9a",
    lineHeight: 1.2,
  },
  userLabel: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    marginTop: 2,
  },
  skeleton: {
    borderRadius: 14,
  },
}));

export default function TableMediaStatus(props) {
  const { loading, attendants, dataini, dataFinal } = props;
  const classes = useStyles();
  const [ticketsData, setTicketsData] = useState(null);
  const [hasNoData, setHasNoData] = useState(false);

  const companyId = localStorage.getItem("companyId");
  const backendUrl = getBackendUrl();

  useEffect(() => {
    getInformations();
  }, [dataini, dataFinal]);

  async function getInformations() {
    try {
      const { data } = await api.get("/dashboard/user-info");

      if (!data || data.length === 0 || !data[0]) {
        setTicketsData(null);
        setHasNoData(true);
        return;
      }

      setTicketsData(data[0]);
      setHasNoData(false);
    } catch (error) {
      console.error("Erro ao buscar informações do atendente em destaque:", error);
      setTicketsData(null);
      setHasNoData(true);
    }
  }

  const queuesWithCount = React.useMemo(() => {
    if (!ticketsData?.queuesWithCount) return [];
    if (typeof ticketsData.queuesWithCount === "string") {
      try {
        return JSON.parse(ticketsData.queuesWithCount);
      } catch {
        return [];
      }
    }
    return Array.isArray(ticketsData.queuesWithCount) ? ticketsData.queuesWithCount : [];
  }, [ticketsData?.queuesWithCount]);

  const getUserInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getProfileImageUrl = () => {
    if (!ticketsData?.profileImage) return null;
    return `${backendUrl}/public/company${companyId}/user/${ticketsData.profileImage}`;
  };

  return !loading ? (
    <Paper elevation={0} className={classes.container}>
      <Box className={classes.header}>
        <EmojiEventsIcon className={classes.headerIcon} />
        <Typography className={classes.headerTitle}>
          Atendente em Destaque
        </Typography>
      </Box>
      <Box className={classes.body}>
        {hasNoData || !ticketsData ? (
          <Box className={classes.emptyMessageWrapper}>
            <Typography className={classes.emptyMessage}>
              Nenhum atendente realizou atendimentos no período selecionado
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.leftSection}>
              <Box className={classes.avatarContainer}>
                {getProfileImageUrl() ? (
                  <Avatar
                    src={getProfileImageUrl()}
                    alt={ticketsData?.name || "Usuário"}
                    className={classes.avatar}
                  >
                    {getUserInitials(ticketsData.name)}
                  </Avatar>
                ) : (
                  <Avatar className={classes.avatar}>
                    {ticketsData?.name ? (
                      getUserInitials(ticketsData.name)
                    ) : (
                      <AccountCircleIcon className={classes.avatarIcon} />
                    )}
                  </Avatar>
                )}
              </Box>
              <Box className={classes.userInfo}>
                <Typography className={classes.userName}>
                  {ticketsData?.name || "Usuário"}
                </Typography>
                {queuesWithCount.length > 0 ? (
                  <Box className={classes.queuesContainer}>
                    {queuesWithCount.map((queue, index) => (
                      <Chip
                        key={index}
                        label={`${queue.name} (${queue.count})`}
                        size="small"
                        className={classes.queueChip}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography className={classes.noQueueText}>
                    Sem setor atribuído
                  </Typography>
                )}
              </Box>
            </Box>
            <Box className={classes.rightSection}>
              <Typography className={classes.userCount}>
                {ticketsData?.quantidade || 0}
              </Typography>
              <Typography className={classes.userLabel}>Atendimentos</Typography>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  ) : (
    <Skeleton variant="rect" height={200} className={classes.skeleton} />
  );
}
